// app/(main)/compte/mes-commandes/[id]/page.tsx
"use client";

import { ArrowLeft, CheckCircle, Clock, Truck, Package, CreditCard } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { PageContainer } from "../../../components/ui/PageContainer";
import { useOrder } from "@cabukaka/hooks";

export default function OrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { order, loading } = useOrder({ id: id! });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "delivered":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "shipped":
        return <Truck className="w-5 h-5 text-blue-500" />;
      case "preparing":
      case "confirmed":
        return <Package className="w-5 h-5 text-orange-500" />;
      case "pending":
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case "cancelled":
        return <CreditCard className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-300" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: "En attente",
      confirmed: "Confirmée",
      preparing: "En préparation",
      shipped: "Expédiée",
      delivered: "Livrée",
      cancelled: "Annulée"
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <PageContainer withContainer className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </PageContainer>
    );
  }

  if (!order) {
    return (
      <PageContainer withContainer className="space-y-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Commande non trouvée</p>
          <button
            onClick={() => navigate("/compte/mes-commandes")}
            className="mt-4 text-primary hover:underline"
          >
            Retour aux commandes
          </button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer withContainer className="space-y-6">
      {/* Header */}
      <button
        onClick={() => navigate("/compte/mes-commandes")}
        className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour aux commandes
      </button>

      {/* Infos commande */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 pb-4 border-b border-gray-100">
          <div>
            <p className="text-sm text-gray-500">Commande</p>
            <p className="font-semibold text-gray-900 text-lg">{order.reference}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Date</p>
            <p className="text-gray-900">{new Date(order.createdAt).toLocaleDateString('fr-FR')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Statut</p>
            <span className={`inline-flex px-2 py-1 rounded-full text-xs ${getStatusIcon(order.status).props.className} bg-opacity-10`}>
              {getStatusLabel(order.status)}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total</p>
            <p className="font-bold text-primary text-xl">{order.total.toLocaleString('fr-FR')} FCFA</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-sm text-gray-500">Paiement</p>
            <p className="text-gray-900 capitalize">
              {order.paymentMethod === "mobile_money" ? "Mobile Money" : 
               order.paymentMethod === "bank_transfer" ? "Virement bancaire" : "Paiement à la livraison"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Adresse de livraison</p>
            <p className="text-gray-900">
              {order.client?.deliveryAddress?.address}, {order.client?.deliveryAddress?.city}, {order.client?.deliveryAddress?.country}
            </p>
          </div>
          {order.notes && (
            <div>
              <p className="text-sm text-gray-500">Notes</p>
              <p className="text-gray-900">{order.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Suivi de commande</h3>
        <div className="space-y-4">
          {[
            { status: "pending", label: "Commande confirmée" },
            { status: "confirmed", label: "Paiement validé" },
            { status: "preparing", label: "Préparation en cours" },
            { status: "shipped", label: "Commande expédiée" },
            { status: "delivered", label: "Commande livrée" }
          ].map((step, index) => {
            const isCompleted = order.status !== "cancelled" && 
              (step.status === order.status || 
              (order.status === "delivered" && step.status !== "cancelled") ||
              (order.status === "shipped" && ["pending", "confirmed", "preparing", "shipped"].includes(step.status)) ||
              (order.status === "preparing" && ["pending", "confirmed", "preparing"].includes(step.status)) ||
              (order.status === "confirmed" && ["pending", "confirmed"].includes(step.status)));
            
            const isActive = step.status === order.status;
            
            return (
              <div key={index} className="flex gap-3">
                {isCompleted ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <Clock className="w-5 h-5 text-gray-300" />
                )}
                <div>
                  <p className={`font-medium ${isActive ? "text-primary" : "text-gray-900"}`}>
                    {step.label}
                  </p>
                  {isActive && (
                    <p className="text-sm text-gray-500 mt-1">
                      {order.status === "pending" && "Votre commande a été confirmée"}
                      {order.status === "confirmed" && "Le paiement a été validé"}
                      {order.status === "preparing" && "Nous préparons votre commande"}
                      {order.status === "shipped" && "Votre commande est en route"}
                      {order.status === "delivered" && "Votre commande a été livrée"}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Articles */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Articles commandés</h3>
        <div className="space-y-4">
          {order.items.map((item, index) => (
            <div key={index} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0">
              <div className="relative w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                <img src={item.product?.mainImage} alt={item.product?.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{item.product?.name}</p>
                <p className="text-sm text-gray-500">Quantité: {item.quantity}</p>
                <p className="text-sm text-gray-500">Boutique: {item.shop?.name}</p>
              </div>
              <p className="font-semibold text-gray-900">{item.price.toLocaleString('fr-FR')} FCFA</p>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
          <div className="text-right">
            <p className="text-sm text-gray-500">Sous-total</p>
            <p className="font-semibold">{order.total.toLocaleString('fr-FR')} FCFA</p>
          </div>
        </div>
      </div>

      {/* Bouton action */}
      {order.status !== "cancelled" && (
        <button
          onClick={() => navigate("/products")}
          className="w-full py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors"
        >
          Commander à nouveau
        </button>
      )}
    </PageContainer>
  );
}